import { wechatMpSceneConfig } from '@/config';
import { NEW_USER_TEMP_SHARE_DISTRIBUTOR, PAGE_PATH, TOKEN } from '@/constants';
import { sceneConfig } from '@/local-conf';
import { fetchAddCustomerSourceMiniProgram, fetchDesCode } from '@/services';
import { useAppStore, useAuthStore } from '@/store';
import { CustomJumpType, OutsideComein } from '@/types';
import type { Common, MpApp } from '@/types/global-business';
import { nextTick } from 'vue';
import { businessUtils } from '.';
import { storage } from '../storage';
import outsideComein from './outside-comein';

/**
 * 根据外部场景进入类型执行对应操作
 * @param type - 来源类型
 * @param params - 携带参数
 * @returns
 */
const executeOnShowActions = async (type: OutsideComein, params: any, extra?: any) => {
  await nextTick();

  const actions: Common.RespondAction<OutsideComein> = {
    [OutsideComein.checkinPage]: () => outsideComein.checkinPage(params),
    [OutsideComein.coupon]: () => outsideComein.coupon(params),
    [OutsideComein.couponBundle]: () => outsideComein.couponBundle(params),
    [OutsideComein.customPage]: () => outsideComein.customPage(params),
    [OutsideComein.homePage]: () => outsideComein.homePage(),
    [OutsideComein.commodityGroupPage]: () => outsideComein.commodityGroupPage(),
    [OutsideComein.giftCard]: () => outsideComein.giftCard(params),
    [OutsideComein.distribution]: () => outsideComein.distribution(params),
    [OutsideComein.memberShipPlan]: () => outsideComein.memberShipPlan(params),
    [OutsideComein.memberCenter]: () => outsideComein.memberCenterPage(),
    [OutsideComein.product]: () => outsideComein.product(params),
    [OutsideComein.purchasePremium]: () => outsideComein.purchasePremium(params, extra),
    [OutsideComein.discountActivity]: () => outsideComein.discountActivity(params, extra),
    [OutsideComein.packageDiscountActivity]: () => outsideComein.packageDiscount(params, extra),
    [OutsideComein.fullDiscountActivity]: () => outsideComein.fullDiscountActivity(params, extra),
    [OutsideComein.selectiveGiftActivity]: () => outsideComein.selectiveGiftActivity(params, extra),
    [OutsideComein.searchResult]: () => outsideComein.searchResult(params),
    [OutsideComein.search]: () => outsideComein.search(),
    [OutsideComein.blindBoxActivity]: () => outsideComein.blindBoxActivity(params),
    [OutsideComein.PointsMall]: () => outsideComein.pointsMall(),
    [OutsideComein.forum]: () => outsideComein.forum(),
    [OutsideComein.forumTag]: () => outsideComein.forumTag(params),
    [OutsideComein.shoppingCreditsDeduction]: () =>
      outsideComein.shoppingCreditsDeduction(params, extra),
    [OutsideComein.shoppingCreditsClaim]: () => outsideComein.shoppingCreditsClaim(params, extra),
    [OutsideComein.none]: () => {},
  };

  actions[type as OutsideComein]?.();
};

export default {
  // page-show 处理小程序、短链接等
  show: async (options: App.LaunchShowOption | undefined) => {
    // #ifdef MP-WEIXIN
    if (options) {
      // 二维码、短链接
      const sceneStr = options.query?.scene || null;
      // 好友转发
      const friend = options.query?.friend || null;

      let shareDistributorData: MpApp.shareDistributor | null = null;

      if (sceneStr) {
        console.log(`<二维码、短链接场景>`);
        // 解析场景值
        shareDistributorData = await fetchDesCode(sceneStr);
      } else if (friend) {
        console.log(`<好友转发场景>`);
        shareDistributorData = options.query as MpApp.shareDistributor;
      }

      console.log(`<App onShow 分享分销数据>`, shareDistributorData);

      if (shareDistributorData) {
        // 缓存分享分销数据
        useAppStore().shareDistributorData = shareDistributorData;

        // options存在路径时，不执行类型对应的操作。
        if (`/${options.path}` === PAGE_PATH.home.route) {
          await executeOnShowActions(
            shareDistributorData.shareType,
            shareDistributorData.shareValue,
            !!shareDistributorData.isDetail
          );
        }

        // 兼容onShow已登录时，执行绑定
        if (useAuthStore().isLogin) {
          outsideComein.distribution(shareDistributorData.distributorId);
        } else {
          if (shareDistributorData.distributorId) {
            // 用户未登录的情况下，需缓存用户的分享分销信息
            storage.set(NEW_USER_TEMP_SHARE_DISTRIBUTOR, shareDistributorData);
            console.log(
              `分享分销 => 用户未登录，缓存已设置：`,
              storage.get(NEW_USER_TEMP_SHARE_DISTRIBUTOR)
            );
          }
        }
      }
    }
    // #endif
  },
  /**
   * 根据类型跳转目标场景
   * @param arg - 携带参数，目标值
   * @param type - 跳转类型
   */
  customJump: (arg: any, type: UnionKey.CustomJumpType) => {
    const config: Record<CustomJumpType, () => void> = {
      [CustomJumpType.url]: () => {
        uni.navigateTo({ url: `${PAGE_PATH.web.route}?src=${arg}` });
      },
      [CustomJumpType.productDetails]: () => outsideComein.product(arg),
      [CustomJumpType.activePage]: () => outsideComein.customPage(arg),
      [CustomJumpType.coupon]: () => outsideComein.coupon(arg),
      [CustomJumpType.couponBundle]: () => outsideComein.couponBundle(arg),
      [CustomJumpType.fullDiscount]: () => outsideComein.fullDiscountActivity(arg, 'fetchDetail'),
      [CustomJumpType.discountByQuantity]: () => outsideComein.discountActivity(arg, 'fetchDetail'),
      [CustomJumpType.purchasePremium]: () => outsideComein.purchasePremium(arg, 'fetchDetail'),
      [CustomJumpType.packageDiscountActivity]: () =>
        outsideComein.packageDiscount(arg, 'fetchDetail'),
      [CustomJumpType.memberShipPlan]: () => outsideComein.memberShipPlan(arg),
      [CustomJumpType.membershipPlanListPage]: () => outsideComein.memberShipPlan(''),
      [CustomJumpType.checkinPage]: () => outsideComein.checkinPage(arg),
      [CustomJumpType.smallProgram]: () => outsideComein.miniProgram(arg),
      [CustomJumpType.blindBoxActivity]: () => outsideComein.blindBoxActivity(arg),
      [CustomJumpType.PointsMall]: () => outsideComein.pointsMall(),
      [CustomJumpType.forum]: () => outsideComein.forum(),
      [CustomJumpType.forumTag]: () => outsideComein.forumTag(arg),
      [CustomJumpType.shoppingCreditsDeduction]: () =>
        outsideComein.shoppingCreditsDeduction(arg, 'fetchDetail'),
      [CustomJumpType.shoppingCreditsClaim]: () =>
        outsideComein.shoppingCreditsClaim(arg, 'fetchDetail'),
      [CustomJumpType.blank]: () => {},
    };

    config[type]?.();
  },
  /**
   * 处理分享分销输出
   * @param share
   * @param options
   * @returns
   */
  shareDistributorOutput: (
    share: Page.ShareAppMessageOption,
    options: Page.CustomShareContent & { shareType?: string; shareValue?: string }
  ) => {
    // 判断path是否已包含问号，决定使用?还是&作为连接符
    const connector = options.path?.includes('?') ? '&' : '?';
    let path = `${options.path}${connector}friend=Y&shareType=${options.shareType}&shareValue=${
      options.shareValue
    }&isDetail=${false}&path=`;

    const distributorId = useAppStore().distributorData?.id || '';

    // 来自页面内分享赚按钮
    if (distributorId) {
      path = `${path}&distributorId=${distributorId}`;
    } else {
      console.log(`<分享分销员ID不存在，请检查>`, useAppStore().distributorData);
    }

    options = {
      ...options,
      path,
    };

    console.log(`<转发好友分享>`, options);

    return options;
  },
  /** 获取解析后的分享分销数据 */
  getShareDistributorData: async (options: AnyObject | undefined) => {
    let data;
    // scene参数存在，则是从分享分销跳转而来
    if (options?.scene) {
      data = await businessUtils.outsideComein.parseOptionsQueryScene(options.scene);
    } else if (options?.friend) {
      data = options;
    }
    return data;
  },
  /**
   * 客服
   * @param e
   */
  customerService: (e: any) => {
    uni.redirectTo({ url: `${e.detail.path}?id=${e.detail.query?.id}&crm=${sceneConfig.channel}` });
  },
  mockUser: (mockUserToken: string) => {
    if (mockUserToken) {
      storage.set(TOKEN, {
        value: mockUserToken,
        setTime: new Date().getTime(),
        expiryTime: 86400000,
      });
      console.log(`<已开启用户模拟>`, mockUserToken);
    }
  },
  /**
   * 获取场景值信息并上报
   * @param options
   */
  scene: (options: any) => {
    console.log(`<scene>`, options);

    const scene = options.scene;

    if (scene) {
      const sceneInfo = wechatMpSceneConfig[scene];
      console.log(`<获取场景值信息并上报>`, sceneInfo);
      fetchAddCustomerSourceMiniProgram({
        sourceType: 'wechat',
        sourceValue: sceneInfo.id.toString(),
      });
    }

    // uni.getGroupEnterInfo({
    //   success(res) {
    //     console.log('getGroupEnterInfo-res', res);
    //   },
    //   fail(err) {
    //     console.log('getGroupEnterInfo-err', err);
    //   },
    // });
  },
};
